import {
  IDoctor,
  IHospital,
  IInstitution,
  IKiwiInstitution,
  InstitutionType,
  INursingHome,
  INursingService,
  IPatient,
  IPharmacy,
  ISubunit,
} from '@alberta/konexi-shared';
import { AuthorizedKiwiInstitution } from '@alberta/konexi-shared/dist/institution/authorized-kiwi-institution';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {
  DoctorDB,
  HospitalDB,
  KiwiInstitutionDB,
  NursingHomeDB,
  NursingServiceDB,
  PharmacyDB,
  SubunitDB,
} from 'src/app/common/repository/databases';
import { FaxContact } from 'src/app/shared/models/fax-contact.model';
import { WizardStepItem } from 'src/app/shared/models/wizard-step-item';
import { QueryService } from 'src/app/shared/services/query/query.service';

export interface InstitutionInfo {
  name: string;
  id: string;
  parentInstitutionId: string;
  type: InstitutionType;
}

@Injectable({
  providedIn: 'root',
})
export class InstitutionsService {
  constructor(
    private readonly _queryService: QueryService,
    private readonly _router: Router,
    private readonly _translate: TranslateService
  ) {}

  /**
   * Construct a title for the given institution and institutionType
   */
  public async getInstitutionTitle(institutionId: string, institutionType: InstitutionType): Promise<string> {
    if (!institutionId || !institutionType) {
      return Promise.resolve('');
    }

    switch (institutionType) {
      case InstitutionType.Doctor:
        return this._queryService.get<IDoctor>(institutionId, DoctorDB).then(doctor => {
          return `${doctor.titleShort || ''} ${doctor.firstName || ''} ${doctor.lastName || ''}`;
        });
      case InstitutionType.Hospital:
      case InstitutionType.Pharmacy:
      case InstitutionType.NursingHome:
      case InstitutionType.NursingService:
        return this.getInstitution(institutionId, institutionType).then(institution => {
          return Promise.resolve(`${institution.name || ''}`);
        });
      default:
        return Promise.resolve('');
    }
  }

  /**
   * Returns an IInstitution promise of an institution (note: NOT doctor, this is a special type of institution)
   * @param id institution id
   * @param type institution type
   */
  public async getInstitution(id: string, type: InstitutionType): Promise<IInstitution> {
    switch (type) {
      case InstitutionType.Hospital:
        return await this._queryService.get<IHospital>(id, HospitalDB);
      case InstitutionType.Pharmacy:
        return await this._queryService.get<IPharmacy>(id, PharmacyDB);
      case InstitutionType.NursingHome:
        return await this._queryService.get<INursingHome>(id, NursingHomeDB);
      case InstitutionType.NursingService:
        return await this._queryService.get<INursingService>(id, NursingServiceDB);
      case InstitutionType.Doctor:
        throw new Error('InstitutionType Doctor cannot return an IInstitution. Use getDoctor() instead.');
      default:
        return undefined;
    }
  }

  public async getInstitutionByStringType(institutionId: string, type: string) {
    switch (type.toLowerCase()) {
      case 'hospital':
        return await this._queryService.get<IHospital>(institutionId, HospitalDB);
      case 'pharmacy':
        return await this._queryService.get<IPharmacy>(institutionId, PharmacyDB);
      case 'nursinghome':
        return await this._queryService.get<INursingHome>(institutionId, NursingHomeDB);
      case 'nursingservice':
        return await this._queryService.get<INursingService>(institutionId, NursingServiceDB);
      case 'doctor':
        throw new Error('InstitutionType Doctor cannot return an IInstitution. Use getDoctor() instead.');
      default:
        return undefined;
    }
  }

  private async getInstitutionInformationsByPatient(
    patient: IPatient,
    availableKiwiInstitutions: IKiwiInstitution[]
  ): Promise<InstitutionInfo[]> {
    const institutionInformations: InstitutionInfo[] = [];
    const targetInstitutions: { id: string; type: InstitutionType }[] = [
      { id: patient.hospital?.hospitalId, type: InstitutionType.Hospital },
      { id: patient.pharmacyId, type: InstitutionType.Pharmacy },
      { id: patient.nursingHomeId, type: InstitutionType.NursingHome },
      { id: patient.nursingServiceId, type: InstitutionType.NursingService },
    ].filter(id => id.id !== undefined) as { id: string; type: InstitutionType }[];
    if (!targetInstitutions || targetInstitutions.length === 0) {
      return institutionInformations;
    }
    // filter patient Institutions to only show Kiwi institutions
    const patientKiwiInstitutions = availableKiwiInstitutions.filter(institution =>
      targetInstitutions.some(targetInstitution => targetInstitution.id === institution.institutionId)
    );

    for (const targetInstitution of patientKiwiInstitutions) {
      const institution = await this.getInstitutionByStringType(
        targetInstitution.institutionId,
        InstitutionType[targetInstitution.institutionType]
      );
      if (institution) {
        institutionInformations.push({
          name: institution.name,
          id: institution._id,
          parentInstitutionId: undefined,
          type: targetInstitution.institutionType,
        });
      }
    }

    return institutionInformations;
  }

  private async getSubunitInformationsByPatient(
    patient: IPatient,
    availableKiwiInstitutions: IKiwiInstitution[]
  ): Promise<InstitutionInfo[]> {
    const subUnitsToLoad: { id: string; type: InstitutionType }[] = [
      { id: patient.hospital?.subunitId, type: InstitutionType.Hospital },
      { id: patient.pharmacy?.subunitId, type: InstitutionType.Pharmacy },
      { id: patient.nursingHome?.subunitId, type: InstitutionType.NursingHome },
      { id: patient.nursingService?.subunitId, type: InstitutionType.NursingService },
    ].filter(subUnit => subUnit.id !== undefined) as { id: string; type: InstitutionType }[];

    const institutionInformations: InstitutionInfo[] = [];

    for (const { id: subUnitId, type: institutionType } of subUnitsToLoad) {
      const subUnit = await this.getSubunit(subUnitId);
      if (!subUnit) {
        continue;
      }

      // filter subunits to only show those belonging to Kiwi institutions
      const isKiwiInstitution = availableKiwiInstitutions.some(
        kiwiInstitution => kiwiInstitution.institutionId === subUnit.institutionId
      );
      if (!isKiwiInstitution) {
        continue;
      }

      const parentInstitution = await this.getInstitutionByStringType(
        subUnit.institutionId,
        InstitutionType[institutionType]
      );
      const displayName = `${parentInstitution?.name || ''}, ${subUnit.name}`;

      institutionInformations.push({
        name: displayName,
        id: subUnit._id,
        parentInstitutionId: subUnit.institutionId,
        type: institutionType,
      });
    }

    return institutionInformations;
  }

  private async getPrimaryDoctorInformationByPatient(
    patient: IPatient,
    availableKiwiInstitutions: IKiwiInstitution[]
  ): Promise<InstitutionInfo | undefined> {
    // filter primary doctor to only show Kiwi institutions
    const isKiwiInstitution = availableKiwiInstitutions.some(
      kiwiInstitution => kiwiInstitution.institutionId === patient.primaryDoctorId
    );
    if (!isKiwiInstitution) {
      return undefined;
    }

    const primaryDoctor = await this.getDoctor(patient.primaryDoctorId);
    if (!primaryDoctor) {
      return undefined;
    }

    const title = `${primaryDoctor.title ? primaryDoctor.title : primaryDoctor.titleShort ? primaryDoctor.titleShort : ''}`;

    return {
      name: `${title} ${primaryDoctor.firstName} ${primaryDoctor.lastName}`,
      id: primaryDoctor._id,
      parentInstitutionId: undefined,
      type: InstitutionType.Doctor,
    };
  }

  public async getInstitutionAndSubunitNamesByPatient(patient: IPatient): Promise<InstitutionInfo[]> {
    const availableKiwiInstitutions = await this._queryService.getAll<IKiwiInstitution>(KiwiInstitutionDB);
    if (!availableKiwiInstitutions || availableKiwiInstitutions.length === 0) {
      return [];
    }

    const institutionInformations: InstitutionInfo[] = [];
    const patientInstitutionInformations = await this.getInstitutionInformationsByPatient(
      patient,
      availableKiwiInstitutions
    );
    institutionInformations.push(...patientInstitutionInformations);
    const patientSubunitInformations = await this.getSubunitInformationsByPatient(patient, availableKiwiInstitutions);
    institutionInformations.push(...patientSubunitInformations);
    const doctorInformation = await this.getPrimaryDoctorInformationByPatient(patient, availableKiwiInstitutions);
    if (doctorInformation) {
      institutionInformations.push(doctorInformation);
    }

    return institutionInformations;
  }

  public async getAuthorizedKiwiInstitutionNames(
    authorizedKiwiInstitutions: AuthorizedKiwiInstitution[]
  ): Promise<string[]> {
    if (!authorizedKiwiInstitutions) {
      return [];
    }

    const names: string[] = [];

    for (const kiwiInstitutionInformation of authorizedKiwiInstitutions) {
      if (kiwiInstitutionInformation.institutionType === InstitutionType.Doctor) {
        const doctor: IDoctor = await this.getDoctor(kiwiInstitutionInformation.id);

        if (!doctor) {
          continue;
        }
        const title = `${doctor.title ? doctor.title : doctor.titleShort ? doctor.titleShort : ''}`;
        const institutionTypeName = this.getInstitutionTypeName(InstitutionType.Doctor);

        names.push(`${institutionTypeName}: ${title} ${doctor.firstName} ${doctor.lastName}`);
      } else if (kiwiInstitutionInformation.parentInstitutionId) {
        // is subunit
        const subUnit: ISubunit = await this.getSubunit(kiwiInstitutionInformation.id);
        if (!subUnit) {
          continue;
        }
        const parentInstitution = kiwiInstitutionInformation.institutionType
          ? await this.getInstitutionByStringType(
              subUnit.institutionId,
              InstitutionType[kiwiInstitutionInformation.institutionType]
            )
          : undefined;

        names.push(
          `${this._translate.instant('institutions.crm.subunit')}: ${parentInstitution?.name || ''} ${subUnit.name}`
        );
      } else {
        const institution: IHospital | IPharmacy | INursingHome | INursingService =
          await this.getInstitutionByStringType(
            kiwiInstitutionInformation.id,
            InstitutionType[kiwiInstitutionInformation.institutionType]
          );

        if (!institution) {
          continue;
        }
        const institutionTypeName = this.getInstitutionTypeName(kiwiInstitutionInformation.institutionType);

        names.push(`${institutionTypeName}: ${institution.name}`);
      }
    }
    return names;
  }

  /**
   * Return an IDoctor from the database. Use this instead of getInstitution() for doctors.
   * @param id doctor id
   */
  public async getDoctor(id: string): Promise<IDoctor> {
    return await this._queryService.get<IDoctor>(id, DoctorDB);
  }

  /**
   * Returns an ISubunit from the database.
   * @param id subunit id
   */
  public async getSubunit(id: string): Promise<ISubunit> {
    return await this._queryService.get<ISubunit>(id, SubunitDB);
  }

  /**
   * Construct the WizardStepItems for the given institution and institutiontype
   */
  public getTabItems(institutionType: InstitutionType, hasActivitesRight: number): WizardStepItem[] {
    const tabitems: WizardStepItem[] = [];
    tabitems.push(this.getInstitutionMasterTabItem(institutionType));
    if (institutionType !== InstitutionType.Doctor) {
      tabitems.push(new WizardStepItem('institutions.crm.subunits', 'subunits', 'alberta-subunit'));
    }
    tabitems.push(new WizardStepItem('institutions.crm.institutionContacts', 'contacts', 'alberta-contact-data'));
    tabitems.push(new WizardStepItem('institutions.crm.institutionNotes', 'notes', 'alberta-note'));
    if (
      (institutionType === InstitutionType.NursingHome ||
        institutionType === InstitutionType.Hospital ||
        institutionType === InstitutionType.Doctor ||
        institutionType === InstitutionType.Pharmacy) &&
      hasActivitesRight
    ) {
      tabitems.push(new WizardStepItem('institutions.crm.activities', 'activities', 'alberta-activities'));
    }
    tabitems.push(new WizardStepItem('institutions.crm.documents', 'documents', 'alberta-document'));
    return tabitems;
  }

  private getInstitutionMasterTabItem(institutionType: InstitutionType): WizardStepItem {
    switch (institutionType) {
      case InstitutionType.Doctor:
        return new WizardStepItem('patient.details.sections.masterdata', 'doctor-master', 'alberta-clipboard');
      case InstitutionType.Hospital:
        return new WizardStepItem('patient.details.sections.masterdata', 'hospital-master', 'alberta-clipboard');
      case InstitutionType.Pharmacy:
        return new WizardStepItem('patient.details.sections.masterdata', 'pharmacy-master', 'alberta-clipboard');
      case InstitutionType.NursingHome:
        return new WizardStepItem('patient.details.sections.masterdata', 'nursing-home-master', 'alberta-clipboard');
      case InstitutionType.NursingService:
        return new WizardStepItem('patient.details.sections.masterdata', 'nursing-service-master', 'alberta-clipboard');
    }
  }

  public async getDoctorFaxContacts(id: string): Promise<FaxContact[]> {
    const doctor: IDoctor = await this._queryService.get<IDoctor>(id, DoctorDB);
    const noFax = !doctor.fax && !doctor.draft?.fax;
    if (!doctor || noFax || doctor.archived) {
      return [];
    }
    const title = `${doctor.title ? doctor.title : (doctor.titleShort ?? '')}`;
    const faxContacts: FaxContact[] = [];
    if (doctor.fax) {
      faxContacts.push(
        new FaxContact(
          `${title} ${doctor.firstName} ${doctor.lastName}`.trim(),
          doctor.fax,
          await this.getPostalAddress(doctor),
          'Arzt'
        )
      );
    }
    if (doctor.draft?.fax) {
      faxContacts.push(
        new FaxContact(
          `${title} ${doctor.firstName} ${doctor.lastName}`.trim(),
          doctor.draft.fax,
          await this.getPostalAddress(doctor),
          'Nicht verifiziert: Arzt'
        )
      );
    }
    return faxContacts;
  }

  public async getInstitutionFaxContact(institution: IInstitution): Promise<FaxContact[]> {
    if (institution?.fax && !institution.archived) {
      return [
        new FaxContact(institution.name, institution.fax, await this.getPostalAddress(institution), 'Institution'),
      ];
    } else {
      return [];
    }
  }

  public getInstitutionType(): string {
    const url = this._router.url;
    let type = url.slice(url.indexOf('//') + 2);
    type = type.slice(0, type.indexOf('/'));
    const hyphen = type.indexOf('-');
    type = type.charAt(0).toUpperCase() + type.slice(1);
    if (hyphen > 0) {
      type = type.slice(0, hyphen) + type.charAt(hyphen + 1).toUpperCase() + type.slice(hyphen + 2);
    }
    return type;
  }
  public getInstitutionTypeName(institutionType: InstitutionType): string {
    switch (institutionType) {
      case InstitutionType.Payer:
        return this._translate.instant('institutions.types.payer');
      case InstitutionType.Doctor:
        return this._translate.instant('institutions.types.doctor');
      case InstitutionType.Hospital:
        return this._translate.instant('institutions.types.hospital');
      case InstitutionType.Pharmacy:
        return this._translate.instant('institutions.types.pharmacy');
      case InstitutionType.NursingHome:
        return this._translate.instant('institutions.types.nursingHome');
      case InstitutionType.NursingService:
        return this._translate.instant('institutions.types.nursingService');
      case InstitutionType.JointPractice:
        return this._translate.instant('institutions.types.jointPractice');
      case InstitutionType.Unknown:
        return this._translate.instant('institutions.types.unknown');
      default:
        return '';
    }
  }

  private async getPostalAddress(institution: IInstitution | IDoctor) {
    const postalAddress = `${institution.address}, ${institution.postalCode} ${institution.city}`;
    return postalAddress
      .replace(/undefined/g, '')
      .replace(/\s\s+/g, ' ')
      .trim();
  }
}
